import { useEffect, useState } from "react";
import { FormProvider, useForm, type Resolver } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

import Stack from "@mui/material/Stack";

import {
  CommunityConfigurationAction,
  PortfolioIntegrationType,
} from "@graphql/api";
import { useSaveCommunityConfigurationMutation } from "@redux/portfolios/portfoliosApi";
import { BASE_PORTFOLIOS_PATH } from "@routing/Portfolios";

import { Form } from "@covertree/ui";
import { Toast } from "@covertree/utils";

import { CommunitiesSection } from "./CommunitiesSection";
import { CommunityConfigurationActions } from "./CommunityConfigurationActions";
import { CommunityConfigurationSummary } from "./CommunityConfigurationSummary";
import { OwnersSection } from "./OwnersSection";
import { PolicyDefaultsSection } from "./PolicyDefaultsSection";
import { RentManagerLocationsSection } from "./RentManagerLocationsSection";
import { RentManagerSettingsSection } from "./RentManagerSettingsSection";
import { StatePoliciesSection } from "./StatePoliciesSection";
import {
  createCommunityConfigurationFormValues,
  toSaveCommunityConfigurationInput,
  validateCommunityConfigurationForActivation,
} from "./communitiesConfigurationForm";
import { communityConfigurationSchema } from "./communityConfigurationValidation";

import type {
  CommunityConfiguration,
  CommunityConfigurationIssue,
} from "@graphql/api";
import type { CommunityConfigurationFormValues } from "./communitiesConfigurationForm";

interface CommunityConfigurationFormProps {
  configuration: CommunityConfiguration;
  portfolioId: string;
}

export function CommunityConfigurationForm({
  configuration,
  portfolioId,
}: CommunityConfigurationFormProps) {
  const navigate = useNavigate();
  const [backendIssues, setBackendIssues] = useState<
    CommunityConfigurationIssue[]
  >([]);
  const [clientErrors, setClientErrors] = useState<string[]>([]);
  const [saveConfiguration, { isLoading: isSaving }] =
    useSaveCommunityConfigurationMutation();
  const form = useForm<CommunityConfigurationFormValues>({
    defaultValues: createCommunityConfigurationFormValues(configuration),
    mode: "onBlur",
    resolver: yupResolver(
      communityConfigurationSchema
    ) as Resolver<CommunityConfigurationFormValues>,
  });
  const { clearErrors, getValues, handleSubmit, reset, trigger } = form;

  useEffect(() => {
    reset(createCommunityConfigurationFormValues(configuration));
  }, [configuration, reset]);

  const persist = async (
    action: CommunityConfigurationAction,
    values: CommunityConfigurationFormValues
  ) => {
    try {
      const result = await saveConfiguration({
        configuration: toSaveCommunityConfigurationInput(
          portfolioId,
          action,
          values
        ),
      }).unwrap();
      setBackendIssues(result.issues);
      if (!result.saved) {
        Toast.showError(
          "Configuration could not be activated. Review the highlighted issues."
        );
        return;
      }

      reset(createCommunityConfigurationFormValues(result.configuration));
      if (action === CommunityConfigurationAction.ACTIVATE) {
        Toast.showSuccess("Portfolio configured and activated");
        navigate(`/${BASE_PORTFOLIOS_PATH}`, { replace: true });
      } else {
        Toast.showSuccess("Configuration draft saved");
      }
    } catch (saveError) {
      console.error("Community configuration save failed", { saveError });
      Toast.showError("Unable to save the community configuration");
    }
  };

  const saveDraft = async () => {
    clearErrors();
    setClientErrors([]);
    await persist(CommunityConfigurationAction.SAVE_DRAFT, getValues());
  };

  const validateActivation = async () => {
    const validationErrors =
      validateCommunityConfigurationForActivation(getValues());
    setClientErrors(validationErrors);
    const fieldsAreValid = await trigger();
    return fieldsAreValid && validationErrors.length === 0;
  };

  const activate = async () => {
    if (!(await validateActivation())) return;
    await handleSubmit((values) =>
      persist(CommunityConfigurationAction.ACTIVATE, values)
    )();
  };

  return (
    <FormProvider {...form}>
      <Form onSubmit={(event) => event.preventDefault()}>
        <Stack spacing={3} pb={10}>
          <CommunityConfigurationSummary
            backendIssues={backendIssues}
            clientErrors={clientErrors}
            portfolio={configuration.portfolio}
          />
          <PolicyDefaultsSection />
          {configuration.portfolio.integrationType ===
            PortfolioIntegrationType.RentManager && (
            <>
              <RentManagerSettingsSection />
              <RentManagerLocationsSection portfolioId={portfolioId} />
            </>
          )}
          <OwnersSection portfolio={configuration.portfolio} />
          <CommunitiesSection taxDefaults={configuration.taxDefaults} />
          <StatePoliciesSection taxDefaults={configuration.taxDefaults} />
          <CommunityConfigurationActions
            isSaving={isSaving}
            onActivate={activate}
            onSaveDraft={saveDraft}
            onValidateActivation={validateActivation}
            portfolioName={configuration.portfolio.name}
          />
        </Stack>
      </Form>
    </FormProvider>
  );
}
